/**
 * Utility function to automatically add plenums based on various conditions
 */
import { IBotData } from '../../../../models';
import {
  applyAccessDoorMounting,
  bot_tunnel_configuration_ids,
  createBotSection,
} from '../../../data/bot';

/**
 * Creates a plenum section with access_door accessory
 */
function createPlenumWithAccessDoor(
  air_stream_id: number,
  airflow: number,
  airflow_direction_id: number,
  tunnel_index: number,
  tunnelConfigId: number,
): ReturnType<typeof createBotSection> {
  return createBotSection({
    type: 'plenum',
    name: 'Plenum',
    air_stream_id,
    airflow,
    accessories: [
      {
        type: 'access_door',
        name: 'Access door',
        mounting_plane_id: applyAccessDoorMounting(
          airflow_direction_id,
          bot_tunnel_configuration_ids[tunnelConfigId as 1 | 2 | 3 | 4 | 5],
          tunnel_index,
        ),
        autoAdded: true, // Mark as automatically added
      },
    ],
  });
}

/**
 * Automatically adds plenum sections based on various conditions:
 * 1. Before fan sections: If before a section type === 'fan' we have section type 'filter'
 *    or type 'coil' with duty_id === 2, add plenum before fan section.
 * 2. For opposite direction tunnels: If tunnels_have_SAME_DIRECTION is FALSE:
 *    - Add plenum in second tunnel at index 0 if first section is not plenum
 *    - Add plenum in first tunnel at the end if last section is not plenum
 * All plenums automatically include access_door accessory.
 */
export function addRequiredPlenums(d: IBotData): IBotData {
  const botData: IBotData = JSON.parse(JSON.stringify(d));
  if (!botData?.tunnels) return d;

  botData.tunnels.forEach((tunnel, tunnelIndex) => {
    const sections = tunnel.sections;
    if (!sections?.length) return;

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const prevSection = sections[i - 1];

      /* -------------------------------------------------
       * FILTER RULE
       * Add plenum before filter if:
       * - filter.frame_loading_id === 1
       * - previous section is NOT plenum
       * ------------------------------------------------- */
      const needsPlenumBeforeFilter =
        section.type === 'filter' &&
        section.frame_loading_id === 1 &&
        prevSection?.type !== 'plenum' &&
        prevSection?.type &&
        !(
          prevSection?.type === 'filter' && prevSection?.frame_loading_id === 1
        );

      /* -------------------------------------------------
       * FAN RULE
       * Add plenum before fan if previous section is:
       * - filter
       * - er_wheel
       * - coil with duty_id === 2
       * ------------------------------------------------- */
      const needsPlenumBeforeFan =
        section.type === 'fan' &&
        (prevSection?.type === 'filter' ||
          prevSection?.type === 'er_wheel' ||
          (prevSection?.type === 'coil' && prevSection.duty_id === 2));

      if (!needsPlenumBeforeFilter && !needsPlenumBeforeFan) {
        continue;
      }
      sections.splice(
        i,
        0,
        createPlenumWithAccessDoor(
          tunnel.air_stream_id,
          tunnel.airflow,
          tunnel.airflow_direction_id || 1,
          tunnelIndex,
          botData.tunnel_configuration,
        ),
      );

      i++; // skip inserted plenum to avoid re-processing
    }
  });

  // Condition 2: Handle opposite direction tunnels
  // If tunnels_have_SAME_DIRECTION is FALSE, add plenums at specific positions
  if (
    botData.tunnels &&
    botData.tunnels.length >= 2 &&
    botData.tunnels_have_same_directions === false
  ) {
    const shouldAddOpening =
      botData.tunnel_configuration === 4 || botData.tunnel_configuration === 5;
    // Add plenum in second tunnel (index 1) at position 0 if first section is not plenum
    const secondTunnelIndex = botData.tunnels.findIndex(
      (t) => t.airflow_direction_id === 2,
    );
    const secondTunnel = botData.tunnels[secondTunnelIndex];
    if (secondTunnel && secondTunnel.sections) {
      const shouldAddPlenumAtStart =
        secondTunnel.sections.length === 0 ||
        secondTunnel.sections[0].type !== 'plenum';

      if (shouldAddPlenumAtStart) {
        const plenumSection = createPlenumWithAccessDoor(
          secondTunnel.air_stream_id,
          secondTunnel.airflow,
          secondTunnel.airflow_direction_id || 1,
          secondTunnelIndex,
          botData.tunnel_configuration,
        );
        secondTunnel.sections.unshift(plenumSection);
      }
      secondTunnel.plenum = secondTunnel.sections[0];
      if (shouldAddOpening) {
        secondTunnel.plenum.accessories.push({
          type: 'opening',
          name: 'Opening',
          mounting_plane_id: 4,
          air_stream_id: secondTunnel.air_stream_id,
          autoAdded: true, // Mark as automatically added
        });
      }
    }

    // Add plenum in first tunnel (index 0) at the end if last section is not plenum
    const firstTunnelIndex = botData.tunnels.findIndex(
      (t) => t.airflow_direction_id === 1,
    );
    const firstTunnel = botData.tunnels[firstTunnelIndex];
    if (firstTunnel && firstTunnel.sections) {
      const shouldAddPlenumAtEnd =
        firstTunnel.sections.length === 0 ||
        firstTunnel.sections[firstTunnel.sections.length - 1].type !== 'plenum';
      if (shouldAddPlenumAtEnd) {
        const plenumSection = createPlenumWithAccessDoor(
          firstTunnel.air_stream_id,
          firstTunnel.airflow,
          firstTunnel.airflow_direction_id || 1,
          firstTunnelIndex,
          botData.tunnel_configuration,
        );
        firstTunnel.sections.push(plenumSection);
      }
      firstTunnel.plenum =
        firstTunnel.sections[firstTunnel.sections.length - 1];
      if (shouldAddOpening) {
        firstTunnel.plenum.accessories.push({
          type: 'opening',
          name: 'Opening',
          mounting_plane_id: 3,
          air_stream_id: firstTunnel.air_stream_id,
          autoAdded: true, // Mark as automatically added
        });
      }
    }
  }
  return botData;
}
